import React, { useState, useEffect, useMemo } from 'react';
import { Router, Link as RouterLink } from 'react-router-dom';
import {
    Container,
    Typography,
    Grid,
    Paper,
    Box,
    Button,
    CircularProgress,
    Alert,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText,
    Breadcrumbs,
    Link,
    Chip,
    IconButton,
    Tooltip,
    FormControlLabel,
    Switch,
    InputAdornment,
    Stack
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import HomeIcon from '@mui/icons-material/Home';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { userAPI, memberProfileAPI, membershipTierAPI } from '../../services/api';
import AdminAccessControl from '../common/AdminAccessControl';
import { format, isValid } from 'date-fns';
import { useAuth } from '../../context/AuthContext';

// Define interfaces for our data structures
interface User {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    role: string;
    status: string;
    phone?: string;
    membershipTierName?: string;
    memberships?: UserMembership[];
    createdAt?: string;
}

interface MembershipTier {
    id: string;
    name: string;
    monthlyPrice: number;
    description: string;
}

interface UserMembership {
    id: number;
    membershipTierId: string;
    membershipTier: MembershipTier;
    startDate: string;
    endDate: string;
    status: 'active' | 'expired' | 'cancelled' | 'suspended';
    autoRenew: boolean;
}

interface UserFormData {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    confirmPassword: string;
    role: string;
    phone?: string;
    address?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
    dateOfBirth?: Date | null;
    tenantId?: string;
    membershipTierId: string;
    sendWelcomeEmail: boolean;
}

const initialFormData: UserFormData = {
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'member',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    dateOfBirth: null,
    membershipTierId: '',
    sendWelcomeEmail: true
};

// Define user roles for select dropdown
const userRoles = [
    { value: 'admin', label: 'Admin' },
    { value: 'employee', label: 'Employee' },
    { value: 'member', label: 'Member' },
    { value: 'coach', label: 'Coach' },
    // { value: 'super_admin', label: 'Super Admin' }          //TODO: This should only be available to super admins
];

const UserManagementPage: React.FC = () => {
    const { user: currentUser } = useAuth();
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    // Filter states
    const [searchText, setSearchText] = useState<string>('');
    const [roleFilter, setRoleFilter] = useState<string>('');
    const [statusFilter, setStatusFilter] = useState<string>('');
    const [membershipFilter, setMembershipFilter] = useState<string>('');

    const [dialogOpen, setDialogOpen] = useState<boolean>(false);
    const [formData, setFormData] = useState<UserFormData>(initialFormData);
    const [formErrors, setFormErrors] = useState<Record<string, string>>({});

    const [membershipTiers, setMembershipTiers] = useState<MembershipTier[]>([]);
    const [loadingMemberships, setLoadingMemberships] = useState<boolean>(false);
    const [userMemberships, setUserMemberships] = useState<Record<string, UserMembership[]>>({});

    // Fetch all users
    const fetchUsers = async () => {
        setLoading(true);
        try {
            const response = await userAPI.getUsers();
            const users = response.data;
            setUsers(users);

            // Fetch memberships for each user
            // await fetchUserMemberships(users);

            setError(null);
        } catch (err) {
            console.error('Error fetching users:', err);
            setError('Failed to load users. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Fetch memberships for all users
    const fetchUserMemberships = async (users: User[]) => {
        const memberships: Record<string, UserMembership[]> = {};

        try {
            // Fetch member profiles for each user in parallel
            const membershipPromises = users.map(async (user) => {
                try {
                    const response = await memberProfileAPI.getUserMemberProfiles(user.id);
                    memberships[user.id] = response.data;
                } catch (err) {
                    console.error(`Error fetching member profiles for user ${user.id}:`, err);
                    memberships[user.id] = [];
                }
            });

            await Promise.all(membershipPromises);
            setUserMemberships(memberships);
        } catch (err) {
            console.error('Error fetching user memberships:', err);
        }
    };

    // Fetch membership tiers
    const fetchMembershipTiers = async () => {
        setLoadingMemberships(true);
        try {
            const response = await membershipTierAPI.getMembershipTiers();
            setMembershipTiers(response.data);
        } catch (err) {
            console.error('Error fetching membership tiers:', err);
            setError('Failed to load membership options. Please refresh the page.');
        } finally {
            setLoadingMemberships(false);
        }
    };

    useEffect(() => {
        fetchUsers();
        fetchMembershipTiers();
    }, []);

    // Handle form field changes
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value
        });

        // Clear error for this field if it exists
        if (formErrors[name]) {
            setFormErrors({
                ...formErrors,
                [name]: ''
            });
        }
    };

    // Handle select changes
    const handleSelectChange = (e: SelectChangeEvent<string>) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
    };

    // Handle date changes
    const handleDateChange = (date: Date | null) => {
        setFormData({
            ...formData,
            dateOfBirth: date
        });
        if (formErrors.dateOfBirth) {
            setFormErrors({ ...formErrors, dateOfBirth: '' });
        }
    };

    // Open dialog for creating a new user
    const handleOpenCreateDialog = () => {
        // Set tenant ID from current user for non-super-admins
        const formWithTenant = {
            ...initialFormData
        };

        if (currentUser && currentUser.tenantId && currentUser.role !== 'super_admin') {
            formWithTenant.tenantId = currentUser.tenantId;
        }

        setFormData(formWithTenant);
        setFormErrors({});
        setDialogOpen(true);
    };

    // Close dialog
    const handleCloseDialog = () => {
        setDialogOpen(false);
    };

    // Validate form
    const validateForm = (): boolean => {
        const errors: Record<string, string> = {};

        if (!formData.firstName) errors.firstName = 'First name is required';
        if (!formData.lastName) errors.lastName = 'Last name is required';

        if (!formData.email) {
            errors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            errors.email = 'Email is invalid';
        }

        if (!formData.password) {
            errors.password = 'Password is required';
        } else if (formData.password.length < 6) {
            errors.password = 'Password must be at least 6 characters';
        }

        if (formData.password !== formData.confirmPassword) {
            errors.confirmPassword = 'Passwords do not match';
        }

        if (!formData.role) errors.role = 'Role is required';

        // Date of birth is stored on the member profile, so it is only collected (and required) for members
        if (formData.role === 'member') {
            if (!formData.dateOfBirth || !isValid(formData.dateOfBirth)) {
                errors.dateOfBirth = 'Date of birth is required for members';
            } else if (formData.dateOfBirth > new Date()) {
                errors.dateOfBirth = 'Date of birth cannot be in the future';
            }
        }

        // If no tenant ID is set for non-super-admin
        if (!formData.tenantId && currentUser?.role !== 'super_admin') {
            errors.tenantId = 'Tenant ID is required';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Handle submit for creating a user
    const handleSubmit = async () => {
        if (!validateForm()) return;

        try {
            // Prepare user data for API
            const userData = {
                ...formData,
                // Only members carry a date of birth (sent as a date-only string)
                dateOfBirth: formData.role === 'member' && formData.dateOfBirth && isValid(formData.dateOfBirth)
                    ? format(formData.dateOfBirth, 'yyyy-MM-dd')
                    : undefined,
            };

            // Create user
            const response = await userAPI.createUser(userData);
            setSuccess('User created successfully!');

            // Close dialog and refresh data
            setDialogOpen(false);
            fetchUsers();

            // Clear success message after 5 seconds
            setTimeout(() => {
                setSuccess(null);
            }, 5000);
        } catch (err: any) {
            console.error('Error creating user:', err);
            setError(err.response?.data?.message || 'Failed to create user. Please try again.');
        }
    };

    // Get status color based on user status
    const getStatusColor = (status: string) => {
        switch (status) {
            case 'active': return 'success';
            case 'pending': return 'warning';
            case 'inactive': return 'error';
            case 'suspended': return 'error';
            case 'on_hold': return 'info';
            default: return 'default';
        }
    };

    // Format membership information for display
    const formatMembershipInfo = (userId: string) => {

        if (!userId || !userMemberships) {
            return 'Loading...';
        }

        const memberships = userMemberships[userId];


        if (!memberships || memberships.length === 0) {
            return 'None';
        }

        // Find active memberships first
        const activeMemberships = memberships.filter(m => m.status === 'active');

        if (activeMemberships.length > 0) {
            if (activeMemberships.length === 1) {
                return activeMemberships[0].membershipTier?.name || 'Unknown';
            } else {
                return `${activeMemberships.length} Active`;
            }
        }

        // If no active memberships, show the most recent one
        const sortedMemberships = memberships.sort((a, b) =>
            new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
        );

        const latestMembership = sortedMemberships[0];
        if (latestMembership?.membershipTier?.name) {
            return `${latestMembership.membershipTier.name} (${latestMembership.status})`;
        }

        return 'Unknown';
    };

    // Get membership status color
    const getMembershipStatusColor = (userId: string) => {
        const memberships = userMemberships[userId];

        if (!memberships || memberships.length === 0) {
            return 'default';
        }

        const activeMemberships = memberships.filter(m => m.status === 'active');
        if (activeMemberships.length > 0) {
            return 'success';
        }

        const expiredMemberships = memberships.filter(m => m.status === 'expired');
        if (expiredMemberships.length > 0) {
            return 'warning';
        }

        return 'error';
    };

    // Filter users based on search and filter criteria
    const filteredUsers = useMemo(() => {
        let filtered = [...users];

        // Text search (name and email)
        if (searchText) {
            const searchLower = searchText.toLowerCase();
            filtered = filtered.filter(user =>
                `${user.firstName} ${user.lastName}`.toLowerCase().includes(searchLower) ||
                user.email.toLowerCase().includes(searchLower)
            );
        }

        // Role filter
        if (roleFilter) {
            filtered = filtered.filter(user => user.role === roleFilter);
        }

        // Status filter
        if (statusFilter) {
            filtered = filtered.filter(user => user.status === statusFilter);
        }

        // Membership filter
        if (membershipFilter) {
            if (membershipFilter === 'none') {
                filtered = filtered.filter(user => {
                    const memberships = userMemberships[user.id];
                    return !memberships || memberships.length === 0;
                });
            } else if (membershipFilter === 'active') {
                filtered = filtered.filter(user => {
                    const memberships = userMemberships[user.id];
                    return memberships && memberships.some(m => m.status === 'active');
                });
            } else if (membershipFilter === 'inactive') {
                filtered = filtered.filter(user => {
                    const memberships = userMemberships[user.id];
                    return memberships && memberships.length > 0 &&
                        !memberships.some(m => m.status === 'active');
                });
            }
        }

        return filtered;
    }, [users, userMemberships, searchText, roleFilter, statusFilter, membershipFilter]);

    // Clear all filters
    const clearFilters = () => {
        setSearchText('');
        setRoleFilter('');
        setStatusFilter('');
        setMembershipFilter('');
    };

    // Get unique roles from users
    const getUniqueRoles = () => {
        const roles = users.map(user => user.role);
        return [...new Set(roles)].sort();
    };

    // Get unique statuses from users
    const getUniqueStatuses = () => {
        const statuses = users.map(user => user.status);
        return [...new Set(statuses)].sort();
    };

    // Define columns for DataGrid
    const columns: GridColDef[] = [
        {
            field: 'firstName',
            headerName: 'Name',
            width: 300,
            renderCell: (params: GridRenderCellParams) => {
                const { firstName, lastName } = params.row;
                const fullName = `${firstName || ''} ${lastName || ''}`.trim();
                return (
                    <RouterLink
                        to={`/users/${params.row.id}`}
                        style={{ 
                            textDecoration: 'none',
                            color: 'primary.main',
                        }}
                    >
                        {fullName}
                    </RouterLink>
                );
            },
        },
        {
            field: 'email',
            headerName: 'Email',
            width: 350,
        },
        {
            field: 'role',
            headerName: 'Role',
            width: 120,
            renderCell: (params: GridRenderCellParams) => (
                <Chip
                    label={params.value?.replace('_', ' ') || ''}
                    size="small"
                    color={params.value === 'admin' ? 'primary' :
                        params.value === 'employee' ? 'secondary' : 'default'}
                />
            ),
        },
        {
            field: 'status',
            headerName: 'Status',
            width: 120,
            renderCell: (params: GridRenderCellParams) => (
                <Chip
                    label={params.value?.replace('_', ' ') || ''}
                    size="small"
                    color={getStatusColor(params.value) as any}
                />
            ),
        },
        // {
        //     field: 'id',
        //     headerName: 'Membership',
        //     width: 180,
        //     renderCell: (params: GridRenderCellParams) => {
        //         const membershipInfo = formatMembershipInfo(params.value);
        //         return membershipInfo;
        //     },
        //     sortable: false, // Disable sorting for membership since it's complex
        // },
        {
            field: 'createdAt',
            headerName: 'Created Date',
            width: 150,
            renderCell: (params: GridRenderCellParams) => {
                if (!params.value) return 'N/A';
                try {
                    const date = new Date(params.value);
                    return date.toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                    });
                } catch (error) {
                    return 'Invalid Date';
                }
            },
        },
    ];

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <AdminAccessControl>
            {/* Breadcrumbs */}
            <Breadcrumbs
                separator={<NavigateNextIcon fontSize="small" />}
                aria-label="breadcrumb"
                sx={{ mb: 3 }}
            >
                <RouterLink
                    className='user-link'
                    style={{ display: 'flex', alignItems: 'center' }}
                    to="/dashboard"
                >
                    <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                    Dashboard
                </RouterLink>
                <Typography color="text.primary">User Management</Typography>
            </Breadcrumbs>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h4" component="h1">
                    User Management
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={handleOpenCreateDialog}
                >
                    Create User
                </Button>
            </Box>

            {/* Success message */}
            {success && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
                    {success}
                </Alert>
            )}

            {/* Error message */}
            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Main content */}
            <Paper sx={{ p: 3 }}>
                {/* Filters */}
                <Stack spacing={2} sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                        <TextField
                            placeholder="Search by name or email..."
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            size="small"
                            sx={{ minWidth: 250 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <FormControl size="small" sx={{ minWidth: 120 }}>
                            <InputLabel>Role</InputLabel>
                            <Select
                                value={roleFilter}
                                onChange={(e) => setRoleFilter(e.target.value)}
                                label="Role"
                            >
                                <MenuItem value="">All Roles</MenuItem>
                                {getUniqueRoles().map((role) => (
                                    <MenuItem key={role} value={role}>
                                        {role.replace('_', ' ')}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl size="small" sx={{ minWidth: 120 }}>
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                label="Status"
                            >
                                <MenuItem value="">All Statuses</MenuItem>
                                {getUniqueStatuses().map((status) => (
                                    <MenuItem key={status} value={status}>
                                        {status.replace('_', ' ')}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl size="small" sx={{ minWidth: 150 }}>
                            <InputLabel>Membership</InputLabel>
                            <Select
                                value={membershipFilter}
                                onChange={(e) => setMembershipFilter(e.target.value)}
                                label="Membership"
                            >
                                <MenuItem value="">All Memberships</MenuItem>
                                <MenuItem value="active">Active Membership</MenuItem>
                                <MenuItem value="inactive">Inactive Membership</MenuItem>
                                <MenuItem value="none">No Membership</MenuItem>
                            </Select>
                        </FormControl>

                        <Button
                            variant="outlined"
                            onClick={clearFilters}
                            startIcon={<ClearIcon />}
                            size="small"
                        >
                            Clear Filters
                        </Button>
                    </Box>
                </Stack>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : users.length === 0 ? (
                    <Box sx={{ textAlign: 'center', p: 4 }}>
                        <Typography variant="body1">
                            No users found. Click "Create User" to add one.
                        </Typography>
                    </Box>
                ) : filteredUsers.length === 0 ? (
                    <Box sx={{ textAlign: 'center', p: 4 }}>
                        <Typography variant="body1">
                            No users match the current filters. Try adjusting your search criteria.
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ height: 700, width: '100%' }}>
                        <DataGrid
                            rows={filteredUsers}
                            columns={columns}
                            getRowId={(row) => row.id}
                            initialState={{
                                pagination: {
                                    paginationModel: { page: 0, pageSize: 10 },
                                },
                            }}
                            pageSizeOptions={[5, 10, 25, 50]}
                            checkboxSelection={false}
                            disableRowSelectionOnClick
                            loading={loading}
                        />
                    </Box>
                )}
            </Paper>

            {/* Create User Dialog */}
            <Dialog
                open={dialogOpen}
                disableEscapeKeyDown={true}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Create New User</DialogTitle>
                <DialogContent>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                            {/* Basic Information Section */}
                            <Grid size={{ xs: 12 }}>
                                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                                    Basic Information
                                </Typography>
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    name="firstName"
                                    label="First Name"
                                    value={formData.firstName}
                                    onChange={handleChange}
                                    fullWidth
                                    required
                                    error={!!formErrors.firstName}
                                    helperText={formErrors.firstName}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    name="lastName"
                                    label="Last Name"
                                    value={formData.lastName}
                                    onChange={handleChange}
                                    fullWidth
                                    required
                                    error={!!formErrors.lastName}
                                    helperText={formErrors.lastName}
                                />
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    name="email"
                                    label="Email"
                                    type="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    fullWidth
                                    required
                                    error={!!formErrors.email}
                                    helperText={formErrors.email}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    name="phone"
                                    label="Phone"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    fullWidth
                                />
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    name="password"
                                    label="Password"
                                    type="password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    fullWidth
                                    required
                                    error={!!formErrors.password}
                                    helperText={formErrors.password}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    name="confirmPassword"
                                    label="Confirm Password"
                                    type="password"
                                    value={formData.confirmPassword}
                                    onChange={handleChange}
                                    fullWidth
                                    required
                                    error={!!formErrors.confirmPassword}
                                    helperText={formErrors.confirmPassword}
                                />
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6 }}>
                                <FormControl fullWidth required error={!!formErrors.role}>
                                    <InputLabel>Role</InputLabel>
                                    <Select
                                        name="role"
                                        value={formData.role}
                                        onChange={handleSelectChange}
                                        label="Role"
                                    >
                                        {userRoles.map((role) => (
                                            <MenuItem key={role.value} value={role.value}>
                                                {role.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {formErrors.role && <FormHelperText>{formErrors.role}</FormHelperText>}
                                </FormControl>
                            </Grid>
                            {formData.role === 'member' && (
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <DatePicker
                                        label="Date of Birth"
                                        value={formData.dateOfBirth}
                                        onChange={handleDateChange}
                                        disableFuture
                                        minDate={new Date(1900, 0, 1)}
                                        slotProps={{
                                            textField: {
                                                fullWidth: true,
                                                required: true,
                                                error: !!formErrors.dateOfBirth,
                                                helperText: formErrors.dateOfBirth
                                            }
                                        }}
                                    />
                                </Grid>
                            )}

                            {/* Address Section */}
                            <Grid size={{ xs: 12, sm: 6 }} sx={{ mt: 2 }}>
                                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                                    Address Information (Optional)
                                </Typography>
                            </Grid>

                            <Grid size={{ xs: 12 }}>
                                <TextField
                                    name="address"
                                    label="Street Address"
                                    value={formData.address}
                                    onChange={handleChange}
                                    fullWidth
                                />
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    name="city"
                                    label="City"
                                    value={formData.city}
                                    onChange={handleChange}
                                    fullWidth
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    name="state"
                                    label="State/Province"
                                    value={formData.state}
                                    onChange={handleChange}
                                    fullWidth
                                />
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    name="zipCode"
                                    label="ZIP/Postal Code"
                                    value={formData.zipCode}
                                    onChange={handleChange}
                                    fullWidth
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    name="country"
                                    label="Country"
                                    value={formData.country}
                                    onChange={handleChange}
                                    fullWidth
                                />
                            </Grid>

                            {/* Membership Section */}
                            <Grid size={{ xs: 12 }} sx={{ mt: 2 }}>
                                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                                    Membership Information
                                </Typography>
                            </Grid>

                            <Grid size={{ xs: 12 }}>
                                <FormControl fullWidth>
                                    <InputLabel>Assign Membership</InputLabel>
                                    <Select
                                        name="membershipTierId"
                                        value={formData.membershipTierId}
                                        onChange={handleSelectChange}
                                        label="Assign Membership"
                                    >
                                        <MenuItem value="">
                                            <em>No Membership</em>
                                        </MenuItem>
                                        {loadingMemberships ? (
                                            <MenuItem disabled>
                                                <CircularProgress size={24} />
                                                Loading memberships...
                                            </MenuItem>
                                        ) : membershipTiers.length === 0 ? (
                                            <MenuItem disabled>No membership tiers available</MenuItem>
                                        ) : (
                                            membershipTiers.map((tier) => (
                                                <MenuItem key={tier.id} value={tier.id}>
                                                    {tier.name} (${tier.monthlyPrice}/month)
                                                </MenuItem>
                                            ))
                                        )}
                                    </Select>
                                    <FormHelperText>
                                        Select a membership to assign to this user (optional)
                                    </FormHelperText>
                                </FormControl>
                            </Grid>

                            {/* Options */}
                            <Grid size={{ xs: 12 }} sx={{ mt: 2 }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formData.sendWelcomeEmail}
                                            onChange={handleChange}
                                            name="sendWelcomeEmail"
                                        />
                                    }
                                    label="Send welcome email with login details"
                                />
                            </Grid>
                        </Grid>
                    </LocalizationProvider>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button onClick={handleSubmit} variant="contained" color="primary">
                        Create User
                    </Button>
                </DialogActions>
            </Dialog>
            </AdminAccessControl>
        </Container>
    );
};

export default UserManagementPage;

/*
In your App.css, add or update:
.user-link {
    color: #1976d2;
    cursor: pointer;
    text-decoration: none;
    transition: color 0.2s;
}
.user-link:hover {
    text-decoration: underline;
    color: #115293;
}
*/
